import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shared/components/ui/card";
import { formatCreatedAgo } from "@/shared/lib/format";
import type { UiTemplateItem } from "@/features/template-builder/queries";
import { FileTextIcon } from "lucide-react";
import { TEMPLATE_CARD_BASE } from "./template-card.styles";

type TemplateCardProps = {
  item: UiTemplateItem;
  isReadOnly?: boolean;
};

export function TemplateCard({ item, isReadOnly = false }: TemplateCardProps) {
  return (
    <Link to={`/template-builder/${item.id}`} className="group block h-full">
      <Card
        className={`${TEMPLATE_CARD_BASE} ${
          isReadOnly
            ? "border-muted-foreground/20 bg-muted/30"
            : "transition-shadow hover:shadow-md"
        }`}
      >
        <CardHeader className="flex-1 space-y-0 p-4 pb-2">
          <div className="flex items-start gap-2">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FileTextIcon className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-[1rem] leading-tight">
                {item.name}
              </CardTitle>
              <CardDescription className="mt-0.5 line-clamp-2">
                {item.context || (
                  <span className="italic text-muted-foreground/60">
                    No context
                  </span>
                )}
              </CardDescription>
            </div>
            {isReadOnly ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                Read-only
              </span>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 capitalize">
              {item.type}
            </span>
            <span>•</span>
            <span>{formatCreatedAgo(item.createdAt)}</span>
          </div>
        </CardContent>
        <CardFooter className="mt-auto p-4 pt-0 text-xs text-primary">
          <span
            className={
              isReadOnly ? "text-muted-foreground" : "group-hover:underline"
            }
          >
            {isReadOnly ? "View template →" : "Edit template →"}
          </span>
        </CardFooter>
      </Card>
    </Link>
  );
}
